import React, { useState, forwardRef, useImperativeHandle } from 'react';

import { Row, Col, Typography, Space, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';

import { Form, SectionWrapper } from 'common/components';
import NutrientForm from './NutrientForm';
import SupplementFormButton from './supplement-label/SupplementFormButton';

import {
  useGetNutritionEnum,
  useNutritionContext,
} from '../hook/useNutritionLabel';
import { parseNumber } from 'utils/parser';

import { getUomTypesAbbreviations } from '../DailyValueConstants';
import { NUTRIENTS_DATA } from '../constants';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';

const NutrientList = forwardRef((props, forwardedRef) => {
  const [form] = Form.useForm();

  const {
    dv = [],
    dailyValuesField,
    productId,
    typeView,
    isEdit = true,
    handleRemoveDV,
  } = props;

  const [selectedNutrient, setSelectedNutrient] = useState(null);

  useImperativeHandle(forwardedRef, () => {
    return {
      validateForm,
    };
  });

  const {
    isAddingNew,
    setIsAddingNew,
    isUpdating,
    setIsUpdating,
    clearEditNutrientState,
  } = useNutritionContext();

  const { basicUomProperties } = useGetNutritionEnum();

  const validateForm = () => {
    form.validateFields();
  };

  const onClickAddNew = () => {
    setSelectedNutrient(null);
    setIsAddingNew(true);
    form.resetFields();
    form.setFieldsValue({ nutrientIdx: null });
  };

  const onAddNew = () => {
    setSelectedNutrient(null);
    setIsAddingNew(false);
    form.resetFields();
  };

  const onCancel = () => {
    setSelectedNutrient(null);
    clearEditNutrientState();
    form.resetFields();
  };

  const clickEditNutrient = (nutrientData, index) => {
    setSelectedNutrient(index);
    setIsAddingNew(false);
    setIsUpdating(true);
    form.setFieldsValue({
      ...nutrientData,
      nutrientIdx: index,
    });
  };

  const styles = {
    inputNumber: {
      width: '100%',
    },
    selectAfter: {
      width: '72px',
    },
    nutrientItem: (index) => {
      return {
        display: 'flex',
        justifyContent: 'flex-end',
        border: '1px solid #ddd',
        padding: '8px 8px',
        borderRadius: '4px',
        borderColor: index === selectedNutrient ? '#238dd9' : '#ddd',
        marginBottom: 3,
      };
    },
    unit: {
      padding: '3px 8px',
      marginRight: '5px',
      background: '#DCDCDC',
      borderRadius: 6,
    },
    form: {
      marginBottom: 10,
    },
    formItem: {
      marginBottom: '0px',
    },
    dv: {
      textAlign: 'center',
    },
    rowMargin: {
      marginBottom: 5,
    },
    addBtnWrapper: {
      marginTop: 30,
    },
    icon: {
      margin: '0 4px',
    },
    editIcon: (disable) => {
      if (disable) {
        return {
          opacity: 0,
          pointerEvent: 'none !important',
          cursorPoiner: 'none',
        };
      }

      return {};
    },
    nutrientFormWrapper: {
      boxShadow: `rgb(50 50 93 / 25%) 0px 2px 5px -1px, rgb(0 0 0 / 30%) 0px 1px 3px -1px`,
      padding: 8,
    },
    precisionCode: {
      wordBreak: 'break-inherit',
      marginBottom: 5,
    },
  };

  const renderItem = (dvItem, index) => {
    const shouldViewForm = selectedNutrient === index && isUpdating;
    return (
      <SectionWrapper
        key={index}
        title={
          shouldViewForm || !isEdit ? null : ( //* isEdit is in QA Spec
            <React.Fragment>
              <EditOutlined
                style={styles.icon}
                onClick={() => clickEditNutrient(dvItem, index)}
              />

              <DeleteOutlined
                style={styles.icon}
                onClick={() => handleRemoveDV(dvItem)}
              />
            </React.Fragment>
          )
        }
        placement='right'
      >
        {shouldViewForm ? (
          <NutrientForm
            selectedNutrient={selectedNutrient}
            method='update'
            form={form}
            dv={dv}
            productId={productId}
            typeView={typeView}
            onCancel={onCancel}
            onAddNew={onAddNew}
            isEdit={isEdit}
          />
        ) : (
          renderItemView(dvItem, index)
        )}
      </SectionWrapper>
    );
  };

  const renderItemView = (dvItem, index) => {
    // Find the corresponding nutrient data object
    const nutrient = NUTRIENTS_DATA.find(
      (item) => item.enumValue === dvItem.nutritionTypeCode
    );

    // Get the enumDisplayName or fallback to the original nutritionTypeCode
    const displayName = nutrient
      ? nutrient.enumDisplayName
      : dvItem.nutritionTypeCode;

    return (
      <Row>
        <Col style={{ flex: 1 }}>
          <Row
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              flex: 1,
              marginBottom: 7,
            }}
          >
            <Col xs={24}>
              <span style={{ wordBreak: 'break-inherit', fontWeight: 'bold' }}>
                {displayName}
              </span>
            </Col>
          </Row>
          <Row>
            <Col span={12}>
              <Space>
                <Typography.Text>
                  {dvItem.measurementPrecisionCode}
                </Typography.Text>
                <Typography.Text strong>
                  {dvItem.quantity != null
                    ? `${parseNumber(
                        dvItem.quantity
                      )}${getUomTypesAbbreviations(
                        basicUomProperties,
                        dvItem.quantityUom
                      )}`
                    : null}
                </Typography.Text>
              </Space>
            </Col>
            <Col span={12} style={{ textAlign: 'right' }}>
              <Space>
                <Typography.Text>
                  {dvItem.dailyValuePrecisionCode}
                </Typography.Text>
                <Typography.Text strong>
                  {dvItem.dailyValueIntakePercent != null
                    ? `${parseNumber(dvItem.dailyValueIntakePercent)}%`
                    : null}
                </Typography.Text>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <SectionWrapper
      title={
        dailyValuesField?.length !== 0 ? (
          <>
            <TraceableCommentContainer
              qaSpecName={'Fact Panel Nutritional Information'}
              qaSpecFieldName='Nutrients'
            >
              Nutrients
            </TraceableCommentContainer>
          </>
        ) : (
          <>
            <TraceableCommentContainer
              qaSpecName={'Fact Panel Nutritional Information'}
              qaSpecFieldName='Nutrients'
            >
              <span style={{ color: 'red' }}>*</span> Nutrients
            </TraceableCommentContainer>
          </>
        )
      }
    >
      {dailyValuesField?.length !== 0 && (
        <React.Fragment>
          <Row style={styles.rowMargin}>
            <Col xs={24}>{dailyValuesField.map(renderItem)}</Col>
          </Row>
        </React.Fragment>
      )}

      {isEdit && !isAddingNew && (
        <Row>
          <Col xs={24}>
            <SupplementFormButton
              text='Add new'
              icon={<PlusOutlined />}
              onClick={onClickAddNew}
              color={!isUpdating && '#0070cc'}
              disabled={isUpdating}
            />
          </Col>
        </Row>
      )}
      {isAddingNew && (
        <NutrientForm
          method='add'
          form={form}
          dv={dv}
          productId={productId}
          typeView={typeView}
          onCancel={onCancel}
          isEdit={isEdit}
        />
      )}
    </SectionWrapper>
  );
});

export default NutrientList;
